import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, LoaderCircle, Mic, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { storeAuthToken } from '@/lib/auth';
import { useAppStore } from '@/hooks/useAppStore';
import { getChottoAuthToken } from '@/services/chotto';
import { useTranslation } from '@/lib/translate';
import { usePostHog } from 'posthog-js/react';
import { Language } from '@/types';
import { useTheme } from '@/components/themes/ThemeProvider';
import { getUserMedia } from '@/lib/userMedia';
import { supportedLanguages } from '@/components/layout/LanguageSelector';

export const GuestLoginPage: React.FC = () => {
  const micInput = useAppStore((state) => state.micInput);
  const setUser = useAppStore((state) => state.setUser);
  const isPreferredLanguageSet = useAppStore((state) => state.isPreferredLanguageSet);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const setPreferredLanguage = useAppStore((state) => state.setPreferredLanguage);
  const setTargetLanguages = useAppStore((state) => state.setTargetLanguages);
  const clientConfig = useAppStore((state) => state.clientConfig);
  const { resolvedTheme } = useTheme();

  const logoSrc = resolvedTheme === 'dark' && clientConfig?.config?.logoDark
    ? clientConfig.config.logoDark
    : clientConfig?.config?.logo;

  const displayLogo = clientConfig?.config?.disableLogoInLoginPage
    ? "/assets/img/chotto-logo.png"
    : logoSrc;

  // CHOTTO users have already seen the new tutorial, white label users need the old tutorial flow
  const isChottoClient = clientConfig?.config?.name === 'CHOTTO';
  const [isLanguageSet, setIsLanguageSet] = useState(isPreferredLanguageSet || isChottoClient);
  const [isMicAllowed, setIsMicAllowed] = useState(false);
  const [mediaComplete, setMediaComplete] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [continueAnyway, setContinueAnyway] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loginError, setLoginError] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useTranslation(preferredLanguage);
  const posthog = usePostHog();

  const updateTargetLanguages = (language: Language) => {
    let languages = [language];
    if (supportedLanguages[0].code !== language) {
      languages.push(supportedLanguages[0].code);
    } else {
      languages.push(supportedLanguages[1].code);
    }
    setTargetLanguages(languages);
  }

  const updatePreferredLanguage = (language: Language) => (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    setPreferredLanguage(language);
    updateTargetLanguages(language);
    setIsLanguageSet(true);
    return false;
  };

  const tutorialSlides = [
    {
      image: '/assets/img/Chotto-Tutorial-01-Mic.jpg',
      description: t('Chotto Chat is a fast and accurate real-time translation app. Just tap to talk!')
    },
    {
      image: '/assets/img/Chotto-Tutorial-02-Scan.jpg',
      description: t('Instantly invite participants to your conversation via a simple QR Code scan.')
    },
    {
      image: '/assets/img/Chotto-Tutorial-03-Talk.jpg',
      description: t('Enjoy using your own device to communicate with new friends. Tap Allow Microphone to get started!')
    }
  ];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      // Swipe left
      setCurrentSlide((prev) => Math.min(prev + 1, tutorialSlides.length - 1));
    }
    if (touchEndX.current - touchStartX.current > 50) {
      // Swipe right
      setCurrentSlide((prev) => Math.max(prev - 1, 0));
    }
  };

  useEffect(() => {
    if (isLanguageSet) {
      updateTargetLanguages(preferredLanguage);
    }
  }, [isLanguageSet]);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        if (navigator.permissions) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (status.state == "granted") {
            setIsMicAllowed(true);
            setMediaComplete(true);
          } else if (isChottoClient) {
            // Auto-request mic permission if coming from new tutorial
            askMediaAccess();
          }
        } else {
          setIsMicAllowed(false);
          if (isChottoClient) {
            askMediaAccess();
          }
        }
      } catch (error) {
        console.error('Error checking microphone permission:', error);
        setIsMicAllowed(false);
        if (isChottoClient) {
          askMediaAccess();
        }
      }
    };

    checkPermission();
  }, []);

  const continueToApp = () => {
    setContinueAnyway(true);
  }

  const askMediaAccess = () => {
    if (mediaError) {
      // on clicking second time, continue to app despite error
      setContinueAnyway(true);
      return;
    }
    const userMediaOptions = micInput
      ? { audio: { deviceId: { exact: micInput } } }
      : { audio: true };

    getUserMedia(userMediaOptions)
      .catch(e => {
        // alert("Microphone access is denied");
        setMediaError(true);
        console.error("getUserMedia error", e);
      })
      .finally(() => setMediaComplete(true));
  };

  useEffect(() => {
    if (mediaError && !continueAnyway) {
      // Auto-continue if coming from new tutorial (CHOTTO client)
      if (isChottoClient) {
        setContinueAnyway(true);
      }
      return
    }
    if (isLanguageSet && mediaComplete) {
      doChottoLogin();
    }
  }, [isLanguageSet, mediaComplete, mediaError, continueAnyway, isChottoClient])

  const doChottoLogin = async () => {
    let token = "";
    try {
      token = await getChottoAuthToken();
    } catch (e) {
      setLoginError(true);
      return;
    }
    const user = storeAuthToken(token);
    setUser(user);
    const storedPath = searchParams.get("returnTo") || "/translate";
    navigate(storedPath);

    if (user) {
      posthog?.identify(user.userId, {
        appId: user.appId,
        provider: user.provider,
        platform: user.platform,
        platformPlayerId: user.platformPlayerId,
        userId: user.userId,
        username: user.firstName,
        lastname: user.lastName,
        preferredLanguage
      });
      posthog?.capture('login', {
        provider: user.provider,
        appId: user.appId,
        platform: user.platform,
        platformPlayerId: user.platformPlayerId,
        userId: user.userId,
        username: user.firstName,
        lastname: user.lastName,
        preferredLanguage
      });
    }
  }

  return (
    <div className="flex h-dvh justify-center bg-neutral-900 p-4 login-container overflow-y-auto">
      <Card className="w-full max-w-sm bg-neutral-900/30 border-neutral-700 text-white rounded-4xl border-0 gap-2 my-auto">
        <CardHeader className={isLanguageSet ? "text-center" : "text-center mt-10"}>
          <CardTitle className="text-2xl uppercase tracking-[4px] text-chotto-gold items-center flex justify-center">
            <img src={displayLogo} width="250" />
          </CardTitle>
          {/* {isLanguageSet && <p className="text-background text-sm">{t("talk a bit, connect a lot")}</p>} */}
        </CardHeader>
        <CardContent className="space-y-1 flex flex-col items-center">
          {/* Error state - shown for all flows */}
          {loginError && (
            <div className="flex flex-col items-center">
              <p className="text-white-300 text-sm text-center max-w-64">
                {t("We were unable to log you in. Please try again.")}
              </p>
              <div className="mt-4 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex items-center justify-center text-white text-md border-white min-w-40 rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 border-1"
                  onClick={() => {
                    const returnTo = searchParams.get("returnTo");
                    if (returnTo) {
                      // Navigate to original URL to restart the full auth flow
                      // This re-triggers white label detection and room joining
                      window.location.href = returnTo;
                    } else {
                      setLoginError(false);
                      doChottoLogin();
                    }
                  }}
                >
                  <RefreshCw className="mr-2" />
                  {t("Retry")}
                </Button>
              </div>
            </div>
          )}
          {/* White label flow - language selection */}
          {!loginError && !isLanguageSet && !isChottoClient && <div className="flex flex-col items-center">
            <div className="mt-4 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105">
              <Button
                type="button"
                variant="secondary"
                className="flex items-center justify-center text-white text-md border-white min-w-40 rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 border-1"
                onClick={updatePreferredLanguage("ja")}
              >
                日本語
                <span className="ml-auto">
                  <ChevronRight />
                </span>
              </Button>
            </div>
            <div className="mt-4 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105">
              <Button
                type="button"
                variant="secondary"
                className="flex items-center justify-center text-white text-md border-white min-w-40 rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 border-1"
                onClick={updatePreferredLanguage("en")}
              >
                English
                <span className="ml-auto flex items-center" >
                  <ChevronRight />
                </span>
              </Button>
            </div>
            <div className="mt-4 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105 mb-5">
              <Button
                type="button"
                variant="secondary"
                className="flex items-center justify-center text-white text-md border-white min-w-40 rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 border-1"
                onClick={updatePreferredLanguage("zh")}
              >
                中文
                <span className="ml-auto">
                  <ChevronRight />
                </span>
              </Button>
            </div>
          </div>}
          {/* White label flow - tutorial carousel */}
          {!loginError && isLanguageSet && !isChottoClient && <div className="flex flex-col items-center mt-2">
            {/* Tutorial Carousel */}
            <div className="w-full max-w-xs">
              <div
                className="relative overflow-hidden rounded-lg"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div
                  className="flex transition-transform duration-300 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {tutorialSlides.map((slide, index) => (
                    <div key={index} className="min-w-full flex flex-col items-center">
                      <img
                        src={slide.image}
                        alt={`Tutorial step ${index + 1}`}
                        className="w-full object-contain rounded-xl max-h-[35dvh]"
                      />
                      <p className="text-foreground text-sm mt-3 text-center px-2">
                        {slide.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-4">
                {tutorialSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${currentSlide === index ? 'bg-chotto-gold' : 'bg-neutral-500'
                      }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center w-60 mt-2">
              {!isMicAllowed
                && <div className="mt-4 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105">
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full flex items-center justify-center text-white text-md border-white min-w-40 rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 border-1"
                    onClick={() => askMediaAccess()}
                  >
                    {t("Allow Microphone")}
                    <span className="ml-auto">
                      <ChevronRight />
                    </span>
                  </Button>
                </div>
              }
              <p className="text-foreground text-xs mt-3">
                {t("To translate spoken language in real time, we need access to your microphone.")}
              </p>
            </div>
          </div>
          }
          {/* CHOTTO flow - just spinner while setting up */}
          {!loginError && isChottoClient && !mediaComplete && (
            <div className="flex flex-col items-center mt-4">
              <LoaderCircle className="animate-spin w-8 h-8 text-chotto-gold" />
              <p className="text-foreground text-sm mt-3">{t("Setting up...")}</p>
            </div>
          )}
          {!loginError && isMicAllowed && (
            <Button
              type="button"
              disabled={true}
              variant="secondary"
              className='w-76 bg-chotto-blue hover:bg-chotto-blue/90 active:bg-chotto-blue/50 text-white mt-5'
              onClick={continueToApp}
            >
              {t("Checking Microphone Access")}
              <LoaderCircle className="inline-flex animate-spin w-4 h-4 mr-1" />
            </Button>
          )}
          {/* White label flow - microphone error state */}
          {!loginError && mediaError && !isMicAllowed && !isChottoClient && (<>
            <div className="flex flex-col items-center justify-center mt-5 mb-5">
              {mediaError && (
                <div className="text-red-300 max-w-64 text-xs mb-2 text-center">
                  {t("Microphone access was denied. Some features may be limited.")}
                </div>
              )}
              <div className="mt-3 bg-gradient-to-r from-orange-300 to-orange-500 rounded-full p-1 hover:scale-105">
                <Button
                  type="button"
                  disabled={mediaComplete && !mediaError}
                  variant="secondary"
                  className="text-white rounded-full bg-transparent hover:bg-transparent shadow-md transition-transform px-9 py-5 flex items-center border-1 border-white"
                  onClick={askMediaAccess}
                >
                  <Mic />
                  {mediaError ? t("Continue without Microphone") : t("Allow Microphone")}
                  {(mediaComplete && !mediaError) && (
                    <LoaderCircle className="inline-flex animate-spin w-4 h-4 mr-1" />
                  )}
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </>)}
        </CardContent>
      </Card>
    </div>
  );
};
